// Typed wrappers for the assistant panel endpoints (apps/api/src/routes/assistant-turns.ts). Signed-in sessions only:
// the browser's cookie session is the credential (API tokens are refused, §9 C9).
import { api } from "./client.ts";
import type { AssistantSessionSummary, AssistantSessionsResponse } from "@maestros/contracts";
import type { AssistantActionOutcome, AssistantClaimVia, AssistantMessagesResponse, AssistantTurnAccepted, AssistantTurnRequest, AssistantTurnState, AssistantUsageResponse } from "./types-assistant.ts";

const enc = encodeURIComponent;
export const listAssistantSessions = (query: { limit?: number; cursor?: string } = {}, signal?: AbortSignal) =>
  api<AssistantSessionsResponse>("/assistant/sessions", { query, ...(signal ? { signal } : {}) });
export const getAssistantSession = (id: string, signal?: AbortSignal) =>
  api<AssistantSessionSummary>(`/assistant/sessions/${enc(id)}`, signal ? { signal } : {});
const action = (messageId: string, index: number, step: "claim" | "executed") => `/assistant/actions/${enc(messageId)}/${index}/${step}`;

/** 202; 409 `version_conflict` (cause `turn_active`) while the session already has an active turn; 422 above 4000 chars. */
export const postAssistantTurn = (body: AssistantTurnRequest, idempotencyKey: string) =>
  api<AssistantTurnAccepted>("/assistant/turns", { method: "POST", body, idempotencyKey });

/** Author-only; 404 for an unknown session or one authored by someone else. */
export const listAssistantMessages = (sessionId: string, afterSequence?: number, signal?: AbortSignal) =>
  api<AssistantMessagesResponse>(`/assistant/sessions/${enc(sessionId)}/messages`, { query: { after_sequence: afterSequence }, ...(signal ? { signal } : {}) });

/** Idempotent: a terminal turn answers its state unchanged. */
export const cancelAssistantTurn = (turnId: string) => api<{ turn_id: string; state: AssistantTurnState }>(`/assistant/turns/${enc(turnId)}/cancel`, { method: "POST", body: {} });

/** 200 once per (message, index); 409 when another tab or click already claimed it (§9 C6). `via`: how it runs (R32). */
export const claimAssistantAction = (messageId: string, index: number, via: AssistantClaimVia) => api<{ claimed: true }>(action(messageId, index, "claim"), { method: "POST", body: { via } });

export const reportAssistantAction = (messageId: string, index: number, body: AssistantActionOutcome) =>
  api<{ recorded: true }>(action(messageId, index, "executed"), { method: "POST", body });

/** Organization admin only (project_id omitted); signed-in sessions only, 403 otherwise (§3.9). */
export const getAssistantUsage = (query: { days: number }, signal?: AbortSignal) =>
  api<AssistantUsageResponse>("/assistant/usage", { query, ...(signal ? { signal } : {}) });

// ---------- D34: API calls of operator actions that had no wrapper yet ----------

/** POST /v1/projects/:id/execution-policy — organization admin session only; audited. */
export const setExecutionPolicy = (projectId: string, enabled: boolean) =>
  api<{ project_id: string; enabled: boolean; workspaces: number; changed: number; requires: string | null }>(`/projects/${enc(projectId)}/execution-policy`, { method: "POST", body: { enabled } });

/** PATCH /v1/tasks/:id — title / description / acceptance criteria (operator). */
export const updateTaskText = (taskId: string, body: { expected_version: number; title?: string; description?: string; acceptance_criteria?: string[] }) =>
  api<{ id: string; version: number }>(`/tasks/${enc(taskId)}`, { method: "PATCH", body });

/** POST /v1/tasks/:id/runs — starts the agents on one task (maintainer); 409 gate_pending with details.cause otherwise. */
export const startTaskRun = (taskId: string) =>
  api<{ task_id: string; started: boolean; execution_id: string | null; reason_code: string | null }>(`/tasks/${enc(taskId)}/runs`, { method: "POST", body: {} });

/** GET /v1/agent-profiles/:id (for its current version). */
export const getAgentProfileVersion = (id: string) => api<{ id: string; version: number }>(`/agent-profiles/${enc(id)}`);

/** GET /v1/routing-configs/:id (its scope, to find the version active there). */
export const getRoutingConfigScope = (id: string) => api<{ id: string; project_id: string | null; version: number; state: string }>(`/routing-configs/${enc(id)}`);
